import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Mic, Square, Settings, MessageCircle, User, Bot, Waves, ChevronDown, ChevronRight } from 'lucide-react';
import { openaiRealtimeService } from '../services/openaiRealtimeService';

const RealtimeAudioOverlay = ({ 
  onClose,
  selectedVoice = 'alloy',
  onVoiceChange,
  existingConversation = [],
  selectedModel = 'gpt-4o-audio-preview',
  currentUser = null,
  onUpdateConversation = null,
  isFromWelcome = false
}) => {
  const [status, setStatus] = useState('initializing');
  const [conversation, setConversation] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [error, setError] = useState(null);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [callStartTime, setCallStartTime] = useState(null);
  const [showNotification, setShowNotification] = useState(null);
  const [conversationInitialized, setConversationInitialized] = useState(false);
  const [callStartMessageSent, setCallStartMessageSent] = useState(false);
  const callStartMessageSentRef = useRef(false);
  
  // Advanced settings state
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [vadThreshold, setVadThreshold] = useState(0.5);
  const [silenceDuration, setSilenceDuration] = useState(1000);
  const [temperature, setTemperature] = useState(0.8);

  // Initialize conversation with existing chat history - but only once to prevent resets
  useEffect(() => {
    if (!conversationInitialized && existingConversation && existingConversation.length > 0) {
      console.log('🚀 Initializing voice conversation with existing history (one-time):', existingConversation.length, 'messages');
      const convertedHistory = existingConversation.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp || Date.now()).getTime(),
        audio: null,
        isFromHistory: true
      }));
      
      setConversation(convertedHistory);
      openaiRealtimeService.setInitialConversation(convertedHistory);
      setConversationInitialized(true);
    }
  }, [existingConversation, conversationInitialized]);

  // Initialize real-time audio
  useEffect(() => {
    const initializeAudio = async () => {
      try {
        // Configure the service with model mapping for compatibility
        openaiRealtimeService.setModel(selectedModel);
        openaiRealtimeService.setVoice(selectedVoice);
        openaiRealtimeService.setUser(currentUser);
        
        console.log(`🎯 Initializing with model: ${selectedModel} -> ${openaiRealtimeService.selectedModel}`);
        
        const success = await openaiRealtimeService.initialize(
          handleNewMessage,
          handleError,
          handleStatusChange,
          handleAudioStart,
          handleAudioEnd
        );

        if (success) {
          setStatus('ready');
          
          // Enable audio by default as user requested
          console.log('🔊 Enabling audio by default after successful initialization');
          setTimeout(() => {
            openaiRealtimeService.enableAudio();
            setAudioEnabled(true);
          }, 200);
          
          // Only auto-start greeting when NOT opened from welcome screen
          // When from welcome screen, the user is explicitly starting audio chat
          if (!isFromWelcome) {
            setTimeout(() => {
              const firstName = currentUser?.name?.split(' ')[0] || currentUser?.email?.split('@')[0] || '';
              
              // Array of natural greeting variations
              const greetings = [
                `Hey ${firstName}! What's on your mind?`,
                `Hi ${firstName}! How can I help you today?`,
                `Hello ${firstName}! What would you like to chat about?`,
                `Hey there ${firstName}! I'm here to help.`,
                `Hi ${firstName}! What can I do for you?`,
                `Hello ${firstName}! Ready when you are.`,
                `Hey ${firstName}! What's going on?`,
                `Hi ${firstName}! I'm all ears.`
              ];
              
              // Add generic greetings if no name available
              if (!firstName) {
                greetings.push(
                  "Hey there! What's on your mind?",
                  "Hi! How can I help you today?",
                  "Hello! What would you like to chat about?",
                  "Hey! I'm here to help.",
                  "Hi! What can I do for you?",
                  "Hello! Ready when you are.",
                  "Hey! What's going on?",
                  "Hi! I'm all ears."
                );
              }
              
              // Pick a random greeting
              const randomGreeting = greetings[Math.floor(Math.random() * greetings.length)];
              
              // Send greeting message automatically
              openaiRealtimeService.sendGreetingMessage(randomGreeting);
            }, 500); // Quick greeting after initialization
          }
        } else {
          setError('Failed to initialize OpenAI Realtime API');
          setStatus('error');
        }
      } catch (err) {
        setError(err.message);
        setStatus('error');
      }
    };

    initializeAudio();

    // Load available voices from the OpenAI Realtime Service
    const loadVoices = () => {
      try {
        const voices = openaiRealtimeService.getAvailableVoices();
        setAvailableVoices(voices);
      } catch (error) {
        console.error('Failed to load voices:', error);
      }
    };
    loadVoices();

    return () => {
      openaiRealtimeService.cleanup();
    };
  }, []);

  // Update voice when it changes
  useEffect(() => {
    openaiRealtimeService.setVoice(selectedVoice);
  }, [selectedVoice]);


  const handleNewMessage = useCallback((message) => {
    console.log('📥 DEBUG: RealtimeAudioOverlay received message:', {
      id: message.id,
      role: message.role,
      content: message.content?.substring(0, 50),
      isStreaming: message.isStreaming,
      originalItemId: message.originalItemId
    });
    
    setConversation(prev => {
      console.log('📥 DEBUG: Current conversation before update:', prev.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content?.substring(0, 30)
      })));
      
      const newConversation = [...prev];
      
      // For voice chat, handle streaming vs new messages differently
      const existingIndex = newConversation.findIndex(msg => msg.id === message.id);
      
      console.log('📥 DEBUG: Looking for message with ID:', message.id);
      console.log('📥 DEBUG: Found existing message at index:', existingIndex);
      
      if (existingIndex >= 0) {
        console.log('📥 DEBUG: Message exists, updating at index', existingIndex);
        console.log('📥 DEBUG: Is streaming?', message.isStreaming);
        
        // If message is streaming, replace it (for real-time transcription updates)
        if (message.isStreaming) {
          console.log('📥 DEBUG: Replacing streaming message');
          newConversation[existingIndex] = message;
        } else {
          console.log('📥 DEBUG: Finalizing complete message');
          // If message is complete and we have an existing one, 
          // this means the streaming is done - finalize it
          newConversation[existingIndex] = { ...message, isStreaming: false };
        }
      } else {
        console.log('📥 DEBUG: New message, adding to conversation');
        // Add as new message (new conversation turn)
        newConversation.push(message);
      }
      
      console.log('📥 DEBUG: Conversation after update:', newConversation.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content?.substring(0, 30)
      })));
      
      return newConversation;
    });

    // Also add to main chat conversation if callback provided and message is complete
    console.log('🔍 Checking if message should go to main chat:', {
      hasCallback: !!onUpdateConversation,
      isFromHistory: message.isFromHistory,
      isStreaming: message.isStreaming,
      messageContent: message.content,
      messageRole: message.role
    });
    
    if (onUpdateConversation && !message.isFromHistory && !message.isStreaming && message.content !== '[Voice Input]') {
      // Skip messages that are marked as truncated/interrupted
      if (message.isTruncated || message.content?.includes('[Interrupted]')) {
        console.log('✂️ Skipping truncated/interrupted message from main chat');
        return;
      }
      
      const chatMessage = {
        id: `voice_${message.role}_${Date.now()}_${Math.random()}`, // Always use unique ID
        role: message.role,
        content: message.content,
        timestamp: new Date(message.timestamp).toISOString(),
        senderId: message.role === 'user' ? currentUser?.id : 'assistant',
        model: message.role === 'assistant' ? selectedModel : undefined,
        isVoiceMessage: true,
        hasAudio: message.audio || false,
        transcription: message.transcription || null,
        voiceCallId: callStartTime, // Link to the voice call session
        originalVoiceId: message.id // Keep original for debugging
      };
      
      console.log('🚀 Adding voice message to main chat:', chatMessage);
      console.log('🚀 Current conversation has', existingConversation?.length || 0, 'messages');
      console.log('🚀 Parent conversation:', existingConversation?.slice(0, 3)?.map(m => ({ role: m.role, content: m.content?.substring(0, 50) })));
      onUpdateConversation(chatMessage);
      console.log('🚀 After adding voice message, checking if persisted...');
    } else if (message.isStreaming) {
      console.log('⏳ Message is streaming, will be added when complete');
    } else if (message.isFromHistory) {
      console.log('📚 Message is from history, skipping');
    } else if (!onUpdateConversation) {
      console.log('❌ No conversation update callback provided');
    } else {
      console.log('❌ Message NOT added to main chat - conditions not met');
    }
  }, [onUpdateConversation, currentUser, selectedModel, callStartTime]);

  const handleError = useCallback((error) => {
    setError(error.message || 'An error occurred');
    setStatus('error');
  }, []);

  const handleStatusChange = useCallback((newStatus) => {
    console.log('Status changed to:', newStatus);
    setStatus(newStatus);
    
    // Track call start when we first get ready - prevent duplicates
    if (newStatus === 'ready' && !callStartTime && !callStartMessageSentRef.current) {
      console.log('🎙️ Starting voice call for the first time');
      const startTime = Date.now();
      setCallStartTime(startTime);
      setCallStartMessageSent(true);
      callStartMessageSentRef.current = true;
      showCallNotification('🎙️ Voice call started', 'success');
      
      // Add call start marker to main conversation only (not voice chat)
      if (onUpdateConversation) {
        const callStartMessage = {
          id: `call_start_${startTime}`,
          role: 'system',
          content: '🎙️ Voice call started',
          timestamp: new Date(startTime).toISOString(),
          senderId: 'system',
          isCallMarker: true,
          callEvent: 'start'
        };
        onUpdateConversation(callStartMessage);
      }
    } else if (newStatus === 'ready' && (callStartTime || callStartMessageSentRef.current)) {
      console.log('🎙️ Service returned to ready state, but call already started - no duplicate message');
    }
  }, [callStartTime, callStartMessageSent, onUpdateConversation]);

  const handleAudioStart = useCallback(() => {
    setIsAISpeaking(true);
  }, []);

  const handleAudioEnd = useCallback(() => {
    setIsAISpeaking(false);
  }, []);

  // Notification helper function
  const showCallNotification = useCallback((message, type = 'info') => {
    setShowNotification({ message, type });
    setTimeout(() => {
      setShowNotification(null);
    }, 3000); // Hide after 3 seconds
  }, []);

  // Handle call end when overlay closes
  const handleClose = useCallback(() => {
    if (callStartTime) {
      const endTime = Date.now();
      const duration = Math.round((endTime - callStartTime) / 1000);
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      const durationText = minutes > 0 
        ? `${minutes}m ${seconds}s`
        : `${seconds}s`;
      
      showCallNotification(`📞 Call ended • Duration: ${durationText}`, 'info');
      
      // Add call end marker to main conversation
      if (onUpdateConversation) {
        const callEndMessage = {
          id: `call_end_${endTime}`,
          role: 'system',
          content: `📞 Voice call ended • Duration: ${durationText}`,
          timestamp: new Date(endTime).toISOString(),
          senderId: 'system',
          isCallMarker: true,
          callEvent: 'end',
          callDuration: duration
        };
        onUpdateConversation(callEndMessage);
      }
      
      // Delay the actual close to show the notification
      setTimeout(() => {
        onClose();
      }, 1500);
    } else {
      onClose();
    }
  }, [callStartTime, onClose, onUpdateConversation]);

  // Enable audio on first user interaction to overcome browser autoplay restrictions
  const enableAudioOnInteraction = useCallback(() => {
    if (!audioEnabled) {
      console.log('🔊 Enabling audio on user interaction');
      openaiRealtimeService.enableAudio();
      setAudioEnabled(true);
      
      // If opened from welcome and no greeting was sent yet, send one now
      if (isFromWelcome && status === 'ready') {
        console.log('🎯 Sending welcome greeting on user interaction');
        setTimeout(() => {
          const firstName = currentUser?.name?.split(' ')[0] || currentUser?.email?.split('@')[0] || '';
          
          // Array of natural greeting variations
          const greetings = [
            `Hey ${firstName}! What's on your mind?`,
            `Hi ${firstName}! How can I help you today?`,
            `Hello ${firstName}! What would you like to chat about?`,
            `Hey there ${firstName}! I'm here to help.`,
            `Hi ${firstName}! What can I do for you?`,
            `Hello ${firstName}! Ready when you are.`,
            `Hey ${firstName}! What's going on?`,
            `Hi ${firstName}! I'm all ears.`
          ];
          
          // Add generic greetings if no name available
          if (!firstName) {
            greetings.push(
              "Hey there! What's on your mind?",
              "Hi! How can I help you today?",
              "Hello! What would you like to chat about?",
              "Hey! I'm here to help.",
              "Hi! What can I do for you?",
              "Hello! Ready when you are.",
              "Hey! What's going on?",
              "Hi! I'm all ears."
            );
          }
          
          // Pick a random greeting
          const randomGreeting = greetings[Math.floor(Math.random() * greetings.length)];
          
          // Send greeting message and ensure audio is ready
          openaiRealtimeService.sendGreetingMessage(randomGreeting);
          // Ensure audio will play when response comes
          setTimeout(() => {
            openaiRealtimeService.enableAudio();
          }, 100);
        }, 300); // Short delay after audio enablement
      }
    }
  }, [audioEnabled, isFromWelcome, status, currentUser]);

  const handleClearConversation = () => {
    openaiRealtimeService.clearConversation();
    setConversation([]);
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'initializing': return 'Starting up...';
      case 'ready': return 'Listening...';
      case 'recording': return 'Listening...';
      case 'processing': return 'Understanding...';
      case 'thinking': return 'Thinking...';
      case 'playing': return 'Speaking...';
      case 'error': return error || 'Something went wrong';
      default: return 'Ready';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'ready': return 'text-green-600';
      case 'recording': return 'text-blue-600';
      case 'processing': return 'text-yellow-600';
      case 'thinking': return 'text-purple-600';
      case 'playing': return 'text-indigo-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="fixed right-4 top-4 w-80 h-[calc(100vh-2rem)] bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col z-40">
      <div className="relative flex flex-col h-full">
        {/* Animated background particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full opacity-20 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>
        
        {/* Call Notification */}
        {showNotification && (
          <div className={`absolute top-20 left-1/2 transform -translate-x-1/2 z-20 px-6 py-3 rounded-full text-white font-medium text-sm shadow-lg transition-all duration-300 ${
            showNotification.type === 'success' ? 'bg-green-500' :
            showNotification.type === 'error' ? 'bg-red-500' :
            'bg-blue-500'
          }`}>
            {showNotification.message}
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 bg-black bg-opacity-20 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${
              status === 'ready' || status === 'recording' ? 'bg-green-400' : 
              status === 'playing' ? 'bg-blue-400' : 'bg-gray-400'
            } animate-pulse`} />
            <div>
              <h2 className="text-xl font-semibold text-white">Voice Assistant</h2>
              <p className="text-sm text-gray-300">
                {selectedModel.replace('gpt-', 'GPT-').replace('-', ' ')}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-3 rounded-full text-white hover:bg-white hover:bg-opacity-20 transition-all"
              title="Settings"
            >
              <Settings className="w-5 h-5" />
            </button>
            
            <button
              onClick={handleClose}
              className="p-3 rounded-full text-white hover:bg-white hover:bg-opacity-20 transition-all"
              title="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="p-4 bg-gray-50 border-b border-gray-200 space-y-4">
            {/* Voice Selection */}
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 min-w-16">Voice:</label>
              <select
                value={selectedVoice}
                onChange={(e) => {
                  onVoiceChange?.(e.target.value);
                  openaiRealtimeService.setVoice(e.target.value);
                }}
                className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {availableVoices.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name} - {voice.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Advanced Settings Toggle */}
            <div className="border-t pt-3">
              <button
                onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                {showAdvancedSettings ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
                Advanced Settings
              </button>
            </div>

            {/* Advanced Settings */}
            {showAdvancedSettings && (
              <div className="space-y-3 pl-6 border-l-2 border-gray-200">
                {/* Voice Activity Detection Threshold */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Voice Detection Sensitivity: {vadThreshold}
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="0.9"
                    step="0.1"
                    value={vadThreshold}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      setVadThreshold(value);
                      openaiRealtimeService.setTurnDetection({ threshold: value });
                    }}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Less Sensitive</span>
                    <span>More Sensitive</span>
                  </div>
                </div>

                {/* Silence Duration */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Silence Duration: {silenceDuration}ms
                  </label>
                  <input
                    type="range"
                    min="500"
                    max="3000"
                    step="100"
                    value={silenceDuration}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      setSilenceDuration(value);
                      openaiRealtimeService.setTurnDetection({ silence_duration_ms: value });
                    }}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Faster Response</span>
                    <span>Longer Pause</span>
                  </div>
                </div>

                {/* AI Response Temperature */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    AI Creativity: {temperature}
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1.0"
                    step="0.1"
                    value={temperature}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      setTemperature(value);
                      openaiRealtimeService.setResponseConfig({ temperature: value });
                    }}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Focused</span>
                    <span>Creative</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Voice Assistant Interface */}
        <div className="flex-1 flex items-center justify-center relative">
          
          {/* Center Voice Visualization */}
          <div className="text-center space-y-8 z-10" onClick={enableAudioOnInteraction}>
            
            {/* Main Voice Circle with Status Animation */}
            <div className="relative flex items-center justify-center">
              
              {/* Outer Pulse Ring */}
              <div className={`absolute w-40 h-40 rounded-full border-2 transition-all duration-300 ${
                status === 'recording' ? 'border-green-400 animate-ping' :
                isAISpeaking ? 'border-blue-400 animate-ping' :
                'border-white border-opacity-30'
              }`} />
              
              {/* Middle Ring */}
              <div className={`absolute w-28 h-28 rounded-full border transition-all duration-300 ${
                status === 'recording' ? 'border-green-300 animate-pulse' :
                isAISpeaking ? 'border-blue-300 animate-pulse' :
                'border-white border-opacity-20'
              }`} />
              
              {/* Main Voice Circle */}
              <div 
                className={`relative w-20 h-20 rounded-full border-4 transition-all duration-500 flex items-center justify-center backdrop-blur-sm cursor-pointer ${
                  status === 'recording'
                    ? 'border-green-400 bg-green-400 bg-opacity-20 shadow-lg shadow-green-400/50' 
                    : isAISpeaking
                    ? 'border-blue-400 bg-blue-400 bg-opacity-20 shadow-lg shadow-blue-400/50 animate-pulse'
                    : status === 'ready' 
                    ? 'border-white bg-white bg-opacity-10 shadow-lg hover:bg-opacity-20' 
                    : 'border-gray-400 bg-gray-400 bg-opacity-10'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  enableAudioOnInteraction();
                }}
                title={!audioEnabled ? "Click to enable audio" : "Audio enabled"}
              >
                
                {/* Status Icon */}
                {status === 'recording' ? (
                  <div className="flex items-center gap-1">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="bg-green-300 rounded-full animate-bounce"
                        style={{
                          width: '3px',
                          height: `${8 + Math.sin(Date.now() * 0.01 + i) * 6}px`,
                          animationDelay: `${i * 0.1}s`,
                          animationDuration: '0.6s'
                        }}
                      />
                    ))}
                  </div>
                ) : isAISpeaking ? (
                  <div className="flex items-center gap-1">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="bg-blue-300 rounded-full animate-bounce"
                        style={{
                          width: '3px',
                          height: `${10 + Math.sin(Date.now() * 0.01 + i) * 8}px`,
                          animationDelay: `${i * 0.1}s`,
                          animationDuration: '0.8s'
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-white bg-opacity-30 flex items-center justify-center">
                    <Mic className="w-5 h-5 text-white opacity-70" />
                  </div>
                )}
              </div>
            </div>

            {/* Status Text */}
            <div className="space-y-4">
              <p className={`text-xl font-medium transition-all duration-300 ${
                status === 'recording' ? 'text-green-300' :
                isAISpeaking ? 'text-blue-300' :
                status === 'ready' ? 'text-white' :
                'text-gray-300'
              }`}>
                {getStatusMessage()}
              </p>
              
              {(status === 'ready' || status === 'recording') && (
                <div className="text-center space-y-2">
                  <p className="text-white text-opacity-70 text-sm animate-pulse">
                    Just start talking...
                  </p>
                  {!audioEnabled && (
                    <p className="text-white text-opacity-50 text-xs animate-pulse">
                      Click to enable audio
                    </p>
                  )}
                </div>
              )}
              {status === 'playing' && (
                <p className="text-white text-opacity-70 text-sm animate-pulse">
                  Interrupt me anytime
                </p>
              )}
            </div>

            {/* Call Duration */}
            {callStartTime && (
              <div className="bg-black bg-opacity-30 backdrop-blur-sm rounded-full px-4 py-2 text-white text-opacity-70 text-sm inline-block">
                Duration: {Math.floor((Date.now() - callStartTime) / 60000)}:
                {String(Math.floor(((Date.now() - callStartTime) % 60000) / 1000)).padStart(2, '0')}
              </div>
            )}
          </div>
        </div>

        {/* Bottom Actions - Minimalist */}
        <div className="relative p-6 mt-auto">
          {conversation.length > 0 && (
            <div className="text-center">
              <button
                onClick={handleClearConversation}
                className="px-4 py-2 text-sm text-white text-opacity-70 hover:text-white hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
                title="Clear conversation"
              >
                Clear Conversation
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RealtimeAudioOverlay;